import * as ko from 'knockout';
import * as $ from 'jquery';
import 'tree';

import {Notifier} from "Core/Common/Notifier";
import {LABELS} from "Core/Components/Translation/Locales"
import {P} from "Core/Common/Promise";
import {Guid} from 'Core/Common/Guid';
import {PATH_RUNNER_MODES} from 'Core/Components/PathRunner/PathRunnerModes';
import {IPathRunnerPin, PathRunner} from 'Core/Components/PathRunner/PathRunner';
import Split from 'split.js';

import {UserVarsManager} from "Core/UserVarsManager/UserVarsManager";
import {GlobalManager, GLOBALS} from 'Core/GlobalManager/GlobalManager';
import {ScreenTypes} from "Core/Common/Enums/ScreenTypes";

import {PathRunnerStore} from "Core/Components/PathRunner/Stores/PathRunnerStore";
import {PathRunnerResponseModel} from "Core/Components/PathRunner/Models/Response/PathRunnerResponseModel";
import {PathRunnerNodeViewModel} from "Core/Components/PathRunner/Models/View/PathRunnerNodeViewModel";
import {PathRunnerViewModel} from "Core/Components/PathRunner/Models/View/PathRunnerViewModel";

import {PathRunnerViewModelMapping} from "Core/Components/PathRunner/Mappings/PathRunnerViewModelMapping";
import {PathRunnerNodeViewModelMapping} from "Core/Components/PathRunner/Mappings/PathRunnerNodeViewModelMapping";
import { PUB_SUB_EVENTS } from 'MenuManager/PubSubEvents';

import Template from "Core/Components/PathRunner/Templates/Template.html";
import ItemTemplate from "Core/Components/PathRunner/Templates/ItemTemplate.html";
import SpecialScreenIconTemplate from "Core/Components/PathRunner/Templates/SpecialScreenIconTemplate.html";


ko.templates["Core/Components/PathRunner/Templates/Template"] = Template;
ko.templates["Core/Components/PathRunner/Templates/ItemTemplate"] = ItemTemplate;
ko.templates["Core/Components/PathRunner/Templates/SpecialScreenIconTemplate"] = SpecialScreenIconTemplate;

export class PathRunnerUnit {
    private static readonly MIN_WIDTH = 180;
    private static readonly MIN_CONTENT_WIDTH = 320;
    private static readonly MOBILE_BREAKPOINT = 768;
    private static readonly DEFAULT_SIZES = [14, 86];
    private static readonly SPLIT_INIT_RETRIES = 10;
    private static readonly GUTTER_SIZE = 10;

    private _entityId: number;
    private _recordId: KnockoutObservable<number>;
    private _expanded: KnockoutObservable<boolean>;
    private _isSpecialScreenExist: KnockoutObservable<boolean>;
    private _model: KnockoutObservable<PathRunnerViewModel>;
    private _userVars: UserVarsManager;
    private _labels = LABELS;

    private _el: HTMLElement;
    private _pathRunner: HTMLElement;
    private _pathRunnerId: string;
    private _treeRendered: boolean;
    private _dataLoaded: KnockoutObservable<boolean>;
    private _pathRunnerNodes: any;
    private _screenId: number;
    private _screenMode: string;
    private _sizes: number[];
    private _pinned: boolean;
    private _split: any;

    constructor(entityId: number, isSpecialScreenExist: boolean) {
        this._pathRunnerId = Guid.NewGuid();
        this._entityId = entityId;
        this._recordId = ko.observable(null);
        this._model = ko.observable(null);

        let isExpanded = this.GetSettingsFromLockStorage();
        this._expanded = ko.observable(isExpanded);
        this._isSpecialScreenExist = ko.observable(isSpecialScreenExist);
        this._dataLoaded = ko.observable(false);
        this._pathRunnerNodes = [];
        this._screenId = 0;
        this._screenMode = PATH_RUNNER_MODES.SelfLink;
        this._sizes = this.GetSizesFromLockStorage();
        this._pinned = false;

        this._dataLoaded.subscribe(dataLoaded => {
            if (dataLoaded) {
                this.RenderTree();
                this.InitSplit();
            }
        });
    }

    SetScreenContext(screenId: number, mode: string): boolean {
        const nextId = screenId || 0;
        const nextMode = mode || PATH_RUNNER_MODES.SelfLink;
        const changed = nextId !== this._screenId || nextMode !== this._screenMode;
        const involvesPath =
            nextMode === PATH_RUNNER_MODES.Path || this._screenMode === PATH_RUNNER_MODES.Path;

        this._screenId = nextId;
        this._screenMode = nextMode;

        return changed && involvesPath;
    }

    HasPathConfig(): boolean {
        return this._screenMode === PATH_RUNNER_MODES.Path;
    }

    ExpandCollapse() {
        this._expanded(!this._expanded());
        this.SaveSettings();
        this.InitSplit();
    }

    OpenSpecialScreen() {
        PubSub.publish(PUB_SUB_EVENTS.GO_TO_SCREEN_BY_TYPE, { EntityId: this._entityId, ScreenType: ScreenTypes.SpecialScreen, IsOpenInModal: false });
    }

    GetTemplateName() {
        return "Core/Components/PathRunner/Templates/Template";
    }

    GetSpecialScreenIconTemplate() {
        return "Core/Components/PathRunner/Templates/SpecialScreenIconTemplate";
    }

    AfterRender(el: HTMLElement[]) {
        this._pathRunner = document.getElementById(this._pathRunnerId);
        this._el = el[0];
        this.RenderTree();
        this.InitSplit();
    }

    private InitSplit(attempt: number = 0) {
        this.DestroySplit();

        if (!this._expanded() || window.innerWidth <= PathRunnerUnit.MOBILE_BREAKPOINT) {
            return;
        }

        const container = document.getElementById(this._pathRunnerId);
        const parent = container?.parentElement;
        const content = (parent?.querySelector(':scope > .content-block')
            || container?.nextElementSibling) as HTMLElement;

        if (!container || !content || content === container) {
            if (attempt < PathRunnerUnit.SPLIT_INIT_RETRIES) {
                requestAnimationFrame(() => this.InitSplit(attempt + 1));
            }
            return;
        }

        container.classList.add('path-runner-split');

        this._split = Split([container, content], {
            sizes: this._sizes && this._sizes.length === 2 ? this._sizes : PathRunnerUnit.DEFAULT_SIZES,
            minSize: [PathRunnerUnit.MIN_WIDTH, PathRunnerUnit.MIN_CONTENT_WIDTH],
            gutterSize: PathRunnerUnit.GUTTER_SIZE,
            snapOffset: 0,
            onDragStart: () => container.classList.add('path-runner-resizing'),
            onDragEnd: (sizes: number[]) => {
                container.classList.remove('path-runner-resizing');
                this._sizes = sizes;
                this.SaveSettings();
            }
        });
    }

    private DestroySplit() {
        const container = document.getElementById(this._pathRunnerId);
        container?.classList.remove('path-runner-split', 'path-runner-resizing');

        if (this._split) {
            this._split.destroy();
            this._split = null;
        }
    }

    private GetCurrentSizes(): number[] {
        return this._split ? this._split.getSizes() : this._sizes;
    }

    private GetSizesFromLockStorage(): number[] {
        if (!this._userVars) this._userVars = UserVarsManager.Instance;

        const settings = this._userVars.GetPathRunnerSettings(this._entityId.toString());
        return settings && settings.Sizes ? settings.Sizes : null;
    }

    LoadNewData(recordId: number) {
        const deferred = P.defer();

        const pathRunnerParentsAmount = GlobalManager.Instance.GetGlobal(GLOBALS.PATH_RUNNER_PARENTS_AMOUNT);
        if (parseInt(pathRunnerParentsAmount) === 0) {
            this.Clear();
            return deferred.promise();
        }

        if (this._pinned) {
            this._pinned = false;
            this._sizes = this.GetSizesFromLockStorage();
            this._expanded(this.GetSettingsFromLockStorage());
        }

        this._dataLoaded(false);
        this._treeRendered = false;

        this._recordId(recordId);

        if (recordId) {
            PathRunnerStore.GetData({EntityId: this._entityId, RecordId: recordId, ScreenId: this._screenId})
                .then((data) => {
                    this.DataLoaded(data);
                    deferred.resolve(null);
                })
                .fail(() => {
                    this.DataLoadFailed.bind(this)
                    deferred.resolve(null);
                });
        }

        return deferred.promise();
    }

    AdoptPin(pin: IPathRunnerPin, recordId: number) {
        const model = pin.Model;

        this._pinned = true;
        this._sizes = pin.Sizes || this._sizes;
        this._expanded(pin.Expanded);
        this._recordId(recordId);
        this._model(model);

        model.CurrentNode = null;
        model.GetNodeByRecordId(recordId, record => {
            if (!model.CurrentNode) {
                model.CurrentNode = record;
            }
        });
        model.SetInitialNode(recordId);

        this._pathRunnerNodes = model.GetNodes();
        this._treeRendered = true;
        this._dataLoaded(false);
        this._dataLoaded(true);
    }

    Clear() {
        if (this._model()) {
            this._model(null);
        }
    }

    private RenderTree() {
        const $treeRoot = $(this._el)
            .next()
            .find('.path-runner-body');

        $treeRoot.jstree('destroy');
        $treeRoot.jstree({
            state: {key: 'path-runner-state'},
            plugins: ['state'],
            core: {
                animation: false,
                data: this.ExtendTree.bind(this),
                multiple: false,
                themes: {
                    icons: false
                }
            }
        });

        $treeRoot
            .on('ready.jstree', (event, data) => this.OpenCurrentNode(data))
            .on('activate_node.jstree ', (event, data) => this.Navigate(data))
            .on('hover_node.jstree', (event, data) => {
                this._model().AddEllipsisTooltip(this._pathRunnerNodes, data);
            })
            .on('dehover_node.jstree', (event, data) => {
                this._model().DestroyEllipsisTooltip(this._pathRunnerNodes, data);
            })
    }

    private ExtendTree(node, callback) {
        if (node.id === '#') {
            if (this._model()) {
                callback.call(node, this._model().ToJson());
            }
        } else {
            this.LoadLinkedRecords(node)
                .then(linkedRecords => this.AddLinkedRecords(linkedRecords, node, callback))
                .fail(this.DataLoadFailed.bind(this));
        }
    }

    private OpenCurrentNode(data) {
        const currentRecord = this._model().CurrentNode;
        if (currentRecord) {
            if (currentRecord.IsSubject || this._pinned) {
                this.OpenNode(data, currentRecord.Guid);
            }
        } else {
            this._model().GetNodeByRecordId(this._recordId(),
                record => {
                    if (!this._model().CurrentNode && (record.IsSubject || this._pinned)) {
                        this.OpenNode(data, record.Guid);
                    }
                });
        }
    }

    private OpenNode(data, guid: string) {
        data.instance._open_to(guid);
        data.instance.open_node(guid);
        data.instance.disable_node(guid);
    }

    private SaveSettings() {
        if (!this._pinned) {
            this.SetSettingsToLockStorage(this._entityId.toString());
            return;
        }

        const pin = PathRunner.Instance.Pin;
        PathRunner.Instance.UpdatePinnedSettings(this._expanded(), this._sizes);

        if (pin && pin.Place) {
            this.SetSettingsToLockStorage(pin.Place);
        }
    }

    private SetSettingsToLockStorage(place: string) {
        this._userVars.SetPathRunnerSettings({
            Place: place,
            Expanded: this._expanded(),
            Sizes: this._sizes || undefined
        });
    }

    private GetSettingsFromLockStorage(): boolean {
        if (!this._userVars) this._userVars = UserVarsManager.Instance;

        let lockStorageSettings = this._userVars.GetPathRunnerSettings(this._entityId.toString());
        return lockStorageSettings ? lockStorageSettings.Expanded : false;
    }

    private DataLoaded(responseModel: PathRunnerResponseModel) {
        if (responseModel) {
            const data = PathRunnerViewModelMapping.MapFromResponse(responseModel);
            this._model(data);
        } else {
            this.Clear();
        }

        if (this._model()) {
            this._model().GetNodeByRecordId(this._recordId(),
                record => {
                    if (!this._model().CurrentNode && record.IsSubject) {
                        this._model().CurrentNode = record;
                    }
                });

            this._model().SetInitialNode(this._recordId());
            this._pathRunnerNodes = this._model().GetNodes();

            this._treeRendered = true;
            this._dataLoaded(true);
        }
    }

    IsPathRunner(): boolean {
        return this._dataLoaded();
    }

    IsSpecialScreenExist(): boolean {
        return this._isSpecialScreenExist();
    }

    private DataLoadFailed(error) {
        new Notifier().Failed(error.message);
    }

    private Navigate(data) {
        const record = data.node.original.record;
        const currentNode = this._model().CurrentNode;

        if (!currentNode || record.Guid !== currentNode.Guid) {
            if (currentNode) {
                data.instance.enable_node(currentNode.Guid);
            }
            this._model().CurrentNode = record;

            if (this._pinned || this.HasPathConfig()) {
                PathRunner.Instance.ClaimNavigation({
                    Model: this._model(),
                    Sizes: this.GetCurrentSizes(),
                    Expanded: this._expanded(),
                    Place: this._entityId.toString()
                });
            }

            PubSub.publish('PATH_RUNNER_NAVIGATE', {
                EntityId: record.EntityId || this._entityId,
                RecordId: record.Id,
                RecordTypeId: record.TypeId,
                IsOpenInModal: false,
                LifeStatusName: record.LifeStatusName
            });
        }

        if ($(".path-runner-tooltip").length) {
            $(".path-runner-tooltip").remove();
        }

    }

    private LoadLinkedRecords(node) {
        const deferredResult = P.defer<PathRunnerNodeViewModel[]>();

        const record = node.original.record;
        let nestedNodes = [];

        this._model().GetNodeByRecordId(record.Id, existingRecord => {
            if (existingRecord.Children.length > 0 && nestedNodes.length === 0) {
                nestedNodes = existingRecord.Children.map(nestedNode => PathRunnerNodeViewModelMapping.Copy(nestedNode));
            }
        });

        if (nestedNodes.length === 0) {
            PathRunnerStore.GetLinkedRecords({EntityId: this._entityId, RecordId: record.Id})
                .then(linkedRecords => {
                    nestedNodes = linkedRecords.map(nestedNode => PathRunnerNodeViewModelMapping.MapFromResponse(nestedNode));
                    deferredResult.resolve(nestedNodes);
                })
                .fail(error => deferredResult.reject(error));
        } else {
            deferredResult.resolve(nestedNodes);
        }

        return deferredResult.promise();
    }

    private AddLinkedRecords(linkedRecords: PathRunnerNodeViewModel[], node, callback) {
        const nestedNodes = linkedRecords.map(nestedNode => nestedNode.ToJson());
        this._model().GetNode(node.id, record => {
            record.AddRange(linkedRecords);
        });
        callback.call(node, nestedNodes);
    }
}